import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Calendar } from 'react-date-range'
import InputMask from 'react-input-mask'
import { useOutsideClick } from '../../../utils/useOutsideClick'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { DateInputProps, ContentInput } from '../../../types/common-types'
import ru from 'date-fns/locale/ru'
import kk from "date-fns/locale/kk"
import en from "date-fns/locale/en-US"
import {
  generateId,
  fromISOdate,
  getDaysInMonth,
  zeroPaddedNumber,
} from '../../../utils/helpers'
import dayjs from 'dayjs'
import styles from './DatePicker.module.scss'
import CheckBoxInput from '../CheckBoxInput/CheckBoxInput'
import calendarIcon from '../../../images/calendar-icon.svg'

function parseTypedDate(input: string): Date | null {
  if (!input || input.includes('_') || input.length !== 10) return null
  const [dayStr, monthStr, yearStr] = input.split('.')
  const day = parseInt(dayStr, 10)
  const month = parseInt(monthStr, 10)
  const year = parseInt(yearStr, 10)
  if (!month || month < 1 || month > 12) return null
  if (!year) return null
  if (!day || day < 1 || day > getDaysInMonth(month, year)) return null
  return new Date(year, month - 1, day)
}

function DatePicker({
  lang,
  accountForm,
  accountFormKey,
  onChange,
  value,
  curr,
  min,
  max,
  className,
  valid,
  required,
  checkLabel,
  checkValue,
  checkChange,
  disabled,
  ...props
}: DateInputProps) {
  const idRef = useRef(generateId(16))
  const invalidClassName = valid ? '' : styles.invalid
  const prefixClassName = className || ''
  const calendarRef = useRef<HTMLDivElement>(null)

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    value ? new Date(value) : undefined
  )
  const [calendarValue, setCalendarValue] = useState(
    value
      ? value.slice(8, 10) + '.' + value.slice(5, 7) + '.' + value.slice(0, 4)
      : ''
  )
  const [openCalendar, setOpenCalendar] = useState(false)
  
  // Функция для определения начальной даты календаря
  const getInitialCalendarDate = useCallback((): Date => {
    // Если есть выбранная дата и она в пределах диапазона - используем её
    if (selectedDate && min && max) {
      if (selectedDate >= min && selectedDate <= max) {
        return selectedDate
      }
    }
    
    // Если есть текущая дата (curr) и она в пределах диапазона - используем её
    if (curr && min && max) {
      if (curr >= min && curr <= max) {
        return curr
      }
    }
    
    // Если есть минимальная дата и она валидна - используем её
    if (min) {
      return min
    }
    
    // Если ничего не подходит, возвращаем текущую дату
    return new Date()
  }, [selectedDate, curr, min, max])

  const [currentCalendarDate, setCurrentCalendarDate] = useState<Date>(
    getInitialCalendarDate()
  )

  const updateDate = useCallback((date: Date) => {
    const result = dayjs(date).format('YYYY-MM-DD')
    setSelectedDate(date)
    setCurrentCalendarDate(date)
    setCalendarValue(dayjs(date).format('DD.MM.YYYY'))
    onChange(result)
  }, [onChange])

  const handleCalendarClose = useCallback(() => {
    setOpenCalendar(false)
  }, [])

  const ref = useOutsideClick(handleCalendarClose)

  useEffect(() => {
    if (value) {
      const date = new Date(value)
      setSelectedDate(date)
      setCalendarValue(dayjs(date).format('DD.MM.YYYY'))
    } else {
      setSelectedDate(undefined)
      setCalendarValue('')
    }
  }, [value])

  // Обновляем currentCalendarDate при изменении min/max
  useEffect(() => {
    setCurrentCalendarDate(getInitialCalendarDate())
  }, [min, max, getInitialCalendarDate])

  // Отслеживаем изменения в select'ах месяца и года
  useEffect(() => {
    if (!openCalendar || !calendarRef.current || !selectedDate) return

    const monthSelect = calendarRef.current.querySelector('.rdrMonthPicker select') as HTMLSelectElement
    const yearSelect = calendarRef.current.querySelector('.rdrYearPicker select') as HTMLSelectElement

    if (!monthSelect || !yearSelect) return

    const handleSelectChange = () => {
      const newMonth = parseInt(monthSelect.value)
      const newYear = parseInt(yearSelect.value)
      
      // Создаем новую дату с выбранными месяцем и годом, но сохраняем день
      const newDate = new Date(
        newYear,
        newMonth,
        selectedDate.getDate()
      )
      
      // Проверяем, что дата действительно изменилась
      if (newYear !== selectedDate.getFullYear() || newMonth !== selectedDate.getMonth()) {
        updateDate(newDate)
      }
    }

    monthSelect.addEventListener('change', handleSelectChange)
    yearSelect.addEventListener('change', handleSelectChange)

    return () => {
      monthSelect.removeEventListener('change', handleSelectChange)
      yearSelect.removeEventListener('change', handleSelectChange)
    }
  }, [openCalendar, selectedDate, updateDate])

  const handleDateSelect = (date: Date) => {
    updateDate(date)
    setOpenCalendar(false)
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value
    setCalendarValue(inputValue)
    const parsed = parseTypedDate(inputValue)
    if (parsed) {
      updateDate(parsed)
    }
  }

  const handleInputBlur = () => {
    if (calendarValue && !parseTypedDate(calendarValue)) {
      setCalendarValue(
        value
          ? dayjs(new Date(value)).format('DD.MM.YYYY')
          : ''
      )
    }
  }

  return (
    <label
      htmlFor={idRef.current}
      className={`${prefixClassName} ${styles.label}`.trim()}
    >
      {required}
      <p className={styles.required}>
        {(accountForm[accountFormKey] as ContentInput).label[lang]}
        {checkLabel && checkChange ? (
            <div className={styles.checkChangeBox}>
              <input
                data-test-id={props.testCheckName}
                id={`dateCheck-${idRef.current}`}
                name={`dateCheck-${idRef.current}`}
                type="checkBox"
                onChange={checkChange}
                checked={checkValue}
              />
              <label htmlFor={`dateCheck-${idRef.current}`}>{checkLabel}</label>
            </div>
          ) : null}
      </p>
      <div className={styles.inputWrapper}>
        <InputMask
          data-test-id={props.testName}
          type="text"
          className={styles.input + ' ' + invalidClassName}
          mask="99.99.9999"
          maskChar={null}
          value={calendarValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          placeholder={
            (accountForm[accountFormKey] as ContentInput)?.placeholder?.[lang]
          }
          disabled={disabled}
        />
        <button
          type="button"
          className={styles.calendarButton}
          onClick={() => setOpenCalendar(true)}
          disabled={disabled}
          aria-label="calendar"
        >
          <img src={calendarIcon} alt="" />
        </button>
      </div>
      {openCalendar && (
        <div ref={ref}>
          <div ref={calendarRef}>
            <Calendar
              data-test-id={props.testName}
              className={styles.Calendar}
              locale={lang === 'eng' ? en : lang === 'kz' ? kk : ru}
              onChange={handleDateSelect}
              date={selectedDate}
              shownDate={currentCalendarDate}
              minDate={min}
              maxDate={max}
            />
          </div>
        </div>
      )}
      {!valid && (accountForm[accountFormKey] as ContentInput).invalidText ? (
        calendarValue.length > 0 ? (
          (accountForm[accountFormKey] as ContentInput).invalidTextCorrect ? (
            <span
              data-test-id={props.testErrorName}
              className={styles.invalidText}
            >
              {(accountForm[accountFormKey] as any).invalidTextCorrect[lang]}
            </span>
          ) : null
        ) : (
          <span
            data-test-id={props.testErrorName}
            className={styles.invalidText}
          >
            {(accountForm[accountFormKey] as any).invalidText[lang]}
          </span>
        )
      ) : null}
    </label>
  )
}

export default DatePicker